import { Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { useAuth, scopeFromRole } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import type { RoleKey } from "@/shared/lib/rbac";
import { clusterRoute } from "@/entities/capability";
import { visibleByPerms } from "@/widgets/appSidebarNav";
import { useKefuPending } from "@/entities/cs-pending";
import {
  CLUSTER_ROUTES,
  RAIL_SECTIONS,
  subKeyOf,
  SUPPORT_GROUPS,
  resolveRailKey,
  type RailKey,
  isSupportRailKey,
} from "./shellNav";

/**
 * 租户端外壳导航 —— **纯 74px 图标轨(2026-07-25 发现流:退第二栏)**。
 *
 * ★为什么退掉常驻/弹出的上下文面板:发现流把二级导航搬进了内容层 —— 首页每群一条横滑带、
 *   点大类进「群页」(/g/:cluster,一次只聚焦一个群的完整卡墙)。于是轨点群直接导航到群页即可,
 *   那个「中间弹出的二级分类」没有存在必要(用户嫌它不和谐)。连带消灭两个旧 bug:
 *   ①「在首页预览内容群后点首页无反应」(旧 preview 态与路由不同步,navigate('/') 同路由空操作);
 *   ②面板头 49px 与顶栏 56px 错位的「多一行」。
 * ★支撑区(资产/组织/设置)是多页管理段,二级导航仍需要,但不再弹侧栏:2026-09-17 起由顶栏
 *   上下文条(ShellContextStrip)统一画「当前轨项的下一层」(此前是内容区顶部的页内横向 tab
 *   SupportSubnav,只在支撑路由出现)。轨点支撑项 → 导航到该组第一页。
 * ★平台超管仍走 AppSidebar(AppNav 里分叉),本组件只表达租户轨。高亮 = 当前路由所属轨项(resolveRailKey)。
 */
export function TenantShellNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions, user } = useAuth();
  const { isImpersonating } = useImpersonationOptional();

  const modules = permissions?.modules;
  const role = ((permissions?.role as RoleKey | undefined) ?? user?.role) as RoleKey | undefined;
  const scope = role ? scopeFromRole(role) : user?.scope;

  // 高亮:纯路由驱动(无 preview 态)。
  const active = resolveRailKey(pathname);

  // 1010 一期补完 B4:客服舱有访客在等人工 → 客服舱所在群的轨项挂红点 + 数字(waitingHuman)。
  const waitingHuman = useKefuPending()?.waitingHuman ?? 0;
  const kefuRailKey = (Object.keys(CLUSTER_ROUTES) as (keyof typeof CLUSTER_ROUTES)[]).find((c) =>
    CLUSTER_ROUTES[c].includes("/ai-kefu")
  );

  const goRail = (key: RailKey) => {
    if (key === "home") {
      navigate("/");
      return;
    }
    // ★判据走 SUPPORT_RAIL_KEYS 单源,不再手写三项字面量:0919 加「帮助」轨时,
    //   旧写法漏改这一行的后果是「点帮助图标什么都不发生」(轨亮不了、页也不跳)。
    if (isSupportRailKey(key)) {
      // 支撑区 → 该组第一页(页内 tab 负责组内切换)。
      const first = visibleByPerms(SUPPORT_GROUPS[key], modules, scope, role, isImpersonating)[0];
      if (first) navigate(first.url);
      return;
    }
    // 能力群 → 群页(发现流工作层)。
    navigate(clusterRoute(key));
  };

  return (
    <nav
      aria-label={t("shell.brand")}
      className="sticky top-0 z-30 flex h-screen w-[74px] shrink-0 flex-col items-center gap-1 border-r bg-card py-3.5"
    >
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label={t("shell.brand")}
        className="mb-1.5 grid h-[42px] w-[42px] shrink-0 place-items-center rounded-[13px] bg-primary text-primary-foreground"
      >
        <Sparkles className="h-5 w-5" />
      </button>
      {RAIL_SECTIONS.filter(
        // 1004 第三轮:支撑区整组在当前身份下一项都看不到(代客时的「设置」= 只有个人中心)→ 轨上不画这一格,
        //   否则点了没反应。权限还没拉到(modules 空)时照画,免得轨闪一下。
        (s) =>
          !(
            s.kind === "support" &&
            modules &&
            isSupportRailKey(s.key) &&
            visibleByPerms(SUPPORT_GROUPS[s.key], modules, scope, role, isImpersonating).length ===
              0
          )
      ).map((s, i, arr) => {
        const Icon = s.icon;
        const isActive = active === s.key;
        const badge = s.key === kefuRailKey && waitingHuman > 0 ? waitingHuman : 0;
        // 支撑区之前:弹簧把它们推到底 + 一道分隔线(只在第一个支撑项前插一次)。
        const firstSupport = s.kind === "support" && arr[i - 1]?.kind !== "support";
        return (
          <Fragment key={s.key}>
            {firstSupport && (
              <>
                <div className="flex-1" />
                <div className="my-1 h-px w-6 bg-border" />
              </>
            )}
            <button
              type="button"
              onClick={() => goRail(s.key)}
              aria-current={isActive ? "page" : undefined}
              /* ★悬浮就说得出这一群能干什么(2026-07-30,7.29 反馈任务 18)。
                 用户报「六项图标字号权重完全一致,是扁平列表」+「用的是营销漏斗术语不是任务
                 语言」——新手想的是「我要做张海报」,映射不到「内容生产」。74px 轨放不下副标,
                 所以副标改成任务语言(cluster.*Sub,已改成「做图 / 做视频 / 写文案」这类)
                 并挂在 title 上;群面板头里也会显示。 */
              title={
                s.kind === "cluster" && subKeyOf(s.key)
                  ? `${t(s.labelKey)} · ${t(subKeyOf(s.key)!)}`
                  : t(s.labelKey)
              }
              className={cn(
                "relative flex h-[46px] w-12 flex-col items-center justify-center gap-0.5 rounded-xl transition-colors",
                // 主次:当前组加重(底色 + 主色 + 粗体),非当前降一档不抢眼 ——
                // 「分得清自己在哪」是这条反馈的另一半。
                isActive
                  ? "bg-primary/12 font-semibold text-primary"
                  : "font-normal text-muted-foreground/80 hover:bg-muted hover:text-foreground"
              )}
            >
              <Icon className={cn("h-[21px] w-[21px]", isActive && "stroke-[2.4]")} />
              <span className="text-2xs leading-none">{t(s.railLabelKey)}</span>
              {badge > 0 && (
                <span
                  data-testid="rail-kefu-badge"
                  aria-label={t("notificationBell.kefuWaitingAria", { count: badge })}
                  className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-2xs font-bold leading-none text-destructive-foreground"
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </button>
          </Fragment>
        );
      })}
    </nav>
  );
}
